import React, { useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, List, ListOrdered } from 'lucide-react';
import './RichTextEditor.css';

const PURIFY_CFG = { USE_PROFILES: { html: true } };

function sanitizeHtml(html) {
  return DOMPurify.sanitize(html || '', PURIFY_CFG);
}

/**
 * Strip HTML tags and return plain text. Use before sending to AI endpoints or exports.
 */
export function stripHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = sanitizeHtml(html);
  // Insert newlines at block boundaries so <p>a</p><p>b</p> → "a\nb" not "ab".
  div.querySelectorAll('p, li, br, h1, h2, h3, h4, h5, h6').forEach(el => {
    el.after(document.createTextNode('\n'));
  });
  return (div.textContent || div.innerText || '').replace(/\n{3,}/g, '\n\n').trim();
}

// Matches opening HTML block tags — avoids misclassifying text like "<$5M" as HTML.
const HTML_BLOCK_RE = /^<(p|ul|ol|li|b|i|br|div|h[1-6]|strong|em)[\s>/]/i;

/**
 * Wrap AI-returned plain text as valid HTML for insertion into the rich editor.
 * If the text already looks like HTML, sanitize and return as-is.
 */
export function toRichContent(text) {
  if (!text) return '';
  if (HTML_BLOCK_RE.test(text.trimStart())) return sanitizeHtml(text);
  return text.split(/\n+/).filter(Boolean).map(l => `<p>${l}</p>`).join('') || `<p>${text}</p>`;
}

export default function RichTextEditor({ value, onChange, onBlur, readOnly, placeholder, minRows = 3 }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: placeholder || '' }),
    ],
    content: sanitizeHtml(value || ''),
    editable: !readOnly,
    immediatelyRender: false,
    onUpdate({ editor: e }) {
      onChange?.(e.getHTML());
    },
    onBlur() {
      onBlur?.();
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly, false);
  }, [editor, readOnly]);

  // Sync external value changes (e.g. AI insertion) without overwriting a focused editor.
  useEffect(() => {
    if (!editor || editor.isFocused) return;
    const clean = sanitizeHtml(value || '');
    if (editor.getHTML() !== clean) {
      editor.commands.setContent(clean, false);
    }
  }, [value, editor]);

  const toolbarItems = [
    { title: 'Bold',          Icon: Bold,        cmd: () => editor?.chain().focus().toggleBold().run(),         active: editor?.isActive('bold')       },
    { title: 'Italic',        Icon: Italic,      cmd: () => editor?.chain().focus().toggleItalic().run(),       active: editor?.isActive('italic')     },
    { title: 'Bullet list',   Icon: List,        cmd: () => editor?.chain().focus().toggleBulletList().run(),   active: editor?.isActive('bulletList') },
    { title: 'Numbered list', Icon: ListOrdered, cmd: () => editor?.chain().focus().toggleOrderedList().run(),  active: editor?.isActive('orderedList')},
  ];

  return (
    <div className="rich-editor" style={{ minHeight: `${minRows * 1.5}em` }}>
      {!readOnly && (
        <div className="rich-editor-toolbar">
          {toolbarItems.map(({ title, Icon, cmd, active }) => (
            <button
              key={title}
              type="button"
              title={title}
              onMouseDown={e => { e.preventDefault(); cmd(); }}
              className={`rich-editor-btn${active ? ' active' : ''}`}
            >
              <Icon className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
